// src/app/components/antenna/blueprint-handoff-card.tsx
//
// Blueprint hand-off card. When the customer has a managed-service Blueprint,
// this renders above Recommended Actions as the curated, primary path: it
// previews the wave organization and drives into the Deep Signal Blueprint.
// It does NOT expand the blueprint inline — clicking navigates to Deep Signal.
//
// Data source: per-customer content from the merged Deep Signal registry, via
// resolveCustomerContentByBrand(brandName). A brand shows this card only when it
// has a blueprint-enabled entry with waves (Sunbit, Iterate, ...). When no
// blueprint exists, this renders nothing, so self-service customers see
// Recommended Actions as the primary with no downgrade. The backend-first path
// (resolveCustomerContent on the run payload) remains the clean long-term source
// for surfaces that have the Deep Signal payload in scope.
//
// NOTE: per-wave completion progress ("0 of N complete") is intentionally left
// off until the blueprint editor backend exists to track it.
import { ArrowRight } from "lucide-react";
import { resolveCustomerContentByBrand } from "../deep-signal/deep-signal-content";

interface BlueprintHandoffCardProps {
  brandName?: string;
  /** Drive into the Deep Signal Blueprint. */
  onOpenBlueprint: () => void;
}

// Wave accent cycles through the brand identity colors.
const WAVE_ACCENTS = ["var(--alpha-teal)", "var(--signal-blue)", "var(--spectral-violet)"];

export function BlueprintHandoffCard({ brandName, onOpenBlueprint }: BlueprintHandoffCardProps) {
  const content = resolveCustomerContentByBrand(brandName);
  const waves = content.blueprint?.waves ?? [];
  if (!content.blueprintEnabled || waves.length === 0) return null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpenBlueprint}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenBlueprint(); }
      }}
      className="relative overflow-hidden hover-lift"
      style={{
        background: "linear-gradient(135deg, rgba(126,95,224,0.06), rgba(0,201,196,0.04) 58%, var(--card))",
        border: "1px solid var(--border)",
        borderRadius: 16,
        boxShadow: "0 1px 2px rgba(13,13,15,.04), 0 12px 32px -16px rgba(126,95,224,.35)",
        padding: 24,
        cursor: "pointer",
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap" style={{ marginBottom: 18 }}>
        <div>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--spectral-violet)",
            }}
          >
            Designed by Upmode
          </span>
          <h3 style={{ fontSize: "var(--text-xl)", fontWeight: "var(--font-weight-semibold)", marginTop: 4 }}>
            Action Blueprint
          </h3>
          <p style={{ color: "var(--muted-foreground)", marginTop: 4, fontSize: "var(--text-sm)" }}>
            Your curated roadmap, and the primary place to start. The actions below are live AI suggestions that complement it.
          </p>
        </div>

        <span
          className="inline-flex items-center gap-1.5 flex-shrink-0"
          style={{
            color: "var(--spectral-violet)",
            fontSize: "var(--text-sm)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          Open your Blueprint
          <ArrowRight size={16} />
        </span>
      </div>

      {/* Wave preview cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {waves.map((wave, i) => {
          const accent = WAVE_ACCENTS[i % WAVE_ACCENTS.length];
          const count = wave.interventions?.length ?? 0;
          return (
            <div
              key={wave.number ?? i}
              style={{
                backgroundColor: "var(--card)",
                border: "1px solid var(--border)",
                borderTop: `3px solid ${accent}`,
                borderRadius: 12,
                padding: "14px 16px",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: accent,
                }}
              >
                Wave {String(wave.number ?? i + 1).padStart(2, "0")}
              </span>
              <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)", marginTop: 6, lineHeight: 1.35 }}>
                {wave.title}
              </div>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginTop: 6 }}>
                {count} intervention{count === 1 ? "" : "s"}
                {wave.velocity_label ? ` · ${wave.velocity_label}` : ""}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
